import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DateField, FormErrorSummary, M, PendingWithChip, SelectField, TextField, TextareaField, applyServerErrors, zRequiredString } from '@/business';
import { Button, Dialog, DialogContent, toast } from '@/design-system';
import { apiErrorMessage, isApiError, type ApiError } from '@/shared/api';
import { todayInDubai } from '@/shared/lib/dates';

import { useChangeResponsibility } from '../../api/mutations';
import { lookupsQuery } from '../../api/queries';

const TYPES: Schema<'ResponsibilityType'>[] = ['customer', 'internal_user', 'internal_role', 'bank', 'external_party'];
const schema = z
  .object({ type: z.enum(['customer', 'internal_user', 'internal_role', 'bank', 'external_party']), userId: z.string().optional(), role: z.string().optional(), department: z.string().optional(), partyId: z.string().optional(), reason: zRequiredString(), dueOn: z.string().optional() })
  .superRefine((v, ctx) => {
    const need = (path: 'userId' | 'role' | 'partyId') => !v[path] && ctx.addIssue({ code: 'custom', path: [path], message: M.required });
    if (v.type === 'internal_user') need('userId');
    if (v.type === 'internal_role') need('role');
    if (v.type === 'external_party') need('partyId');
  });
type Values = z.infer<typeof schema>;

/** Manual change of Pending With. The reason is required and kept in the history. */
export function ResponsibilityDialog({ deal, onClose }: { deal: Schema<'Deal'>; onClose: () => void }) {
  const { t } = useTranslation('deals');
  const lookups = useQuery(lookupsQuery);
  const change = useChangeResponsibility(deal.id);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { type: 'customer', reason: '', department: '', dueOn: '' } });
  const type = useWatch({ control: form.control, name: 'type' });
  const parties = (lookups.data?.externalParties ?? []).filter((p) => !p.bankCode || p.bankCode === deal.bank.code);

  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await change.mutateAsync({
        type: v.type,
        reason: v.reason.trim(),
        ...(v.type === 'internal_user' && v.userId ? { userId: v.userId } : {}),
        ...(v.type === 'internal_role' && v.role ? { role: v.role as 'sales' | 'operations' | 'finance' } : {}),
        ...(v.type === 'bank' ? { bankId: deal.bank.id, ...(v.department?.trim() ? { department: v.department.trim() } : {}) } : {}),
        ...(v.type === 'external_party' && v.partyId ? { partyId: v.partyId } : {}),
        ...(v.dueOn ? { dueOn: v.dueOn } : {}),
      });
      toast.done(t('responsibility.done'));
      onClose();
    } catch (e) {
      if (isApiError(e)) {
        applyServerErrors(form, e, ['userId', 'role', 'partyId', 'reason']);
        setProblem(e);
      }
    }
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        size="form"
        title={t('responsibility.title')}
        description={t('responsibility.body')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={change.isPending} onClick={() => void submit()}>
              {t('actions.change_responsibility.label')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {problem ? <FormErrorSummary title={apiErrorMessage(problem)} /> : null}
          <p className="flex items-center gap-2 text-body text-ink-secondary">
            {t('responsibility.current')} <PendingWithChip pendingWith={deal.responsibility} />
          </p>
          <SelectField control={form.control} name="type" label={t('responsibility.type')} options={TYPES.map((x) => ({ value: x, label: t(`responsibility.types.${x}`) }))} />
          {type === 'internal_user' ? <SelectField control={form.control} name="userId" label={t('responsibility.person')} options={(lookups.data?.users ?? []).map((u) => ({ value: u.id, label: u.role ? `${u.name} · ${u.role}` : u.name }))} placeholder={t('form.selectPlaceholder', { ns: 'common' })} /> : null}
          {type === 'internal_role' ? <SelectField control={form.control} name="role" label={t('responsibility.role')} options={(['operations', 'sales', 'finance'] as const).map((r) => ({ value: r, label: t(`responsibility.roles.${r}`) }))} placeholder={t('form.selectPlaceholder', { ns: 'common' })} /> : null}
          {type === 'bank' ? <TextField control={form.control} name="department" label={t('responsibility.department', { bank: deal.bank.code })} placeholder={t('responsibility.departmentPlaceholder', { bank: deal.bank.code })} optional /> : null}
          {type === 'external_party' ? <SelectField control={form.control} name="partyId" label={t('responsibility.party')} options={parties.map((p) => ({ value: p.id, label: p.bankCode ? `${p.name} · ${p.bankCode}` : p.name }))} placeholder={t('form.selectPlaceholder', { ns: 'common' })} /> : null}
          <TextareaField control={form.control} name="reason" label={t('responsibility.reason')} rows={2} />
          <DateField control={form.control} name="dueOn" label={t('responsibility.dueOn')} optional min={todayInDubai()} />
          <button type="submit" hidden />
        </form>
      </DialogContent>
    </Dialog>
  );
}
